import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { theme } from "shared/lib/styles";

import {
  useAppBarScrollOverEffect,
  useAppBarStickyHeightValue,
} from "scmp-app/components/app-bar/hooks";
import type { ArticleRenderChildProps } from "scmp-app/components/article/article-render";
import { GlobalChecker } from "scmp-app/components/article/article-render/global-checker";
import { useMobileInlineAdSlotIsDisabledFlag } from "scmp-app/components/article/article-render/hooks";
import type { infographicArticleArticle$key } from "scmp-app/queries/__generated__/infographicArticleArticle.graphql";

import {
  AppBarScrollOverTracker,
  AuthorDetail,
  Comment,
  Content,
  FooterAdSlot,
  HeaderAdSlot,
  InfographicArticleContainer,
  InfographicArticleWrapper,
  LandingBanner,
  Left,
  Paywall,
  Social,
  SocialDesktop,
  SocialMobile,
  SpeechWrapper,
  SummaryAndAuthor,
  Topic,
} from "./styles";

export type Props = ArticleRenderChildProps & {
  reference: infographicArticleArticle$key;
};

export const InfographicArticle: FunctionComponent<Props> = ({
  provider,
  reference: reference_,
}) => {
  const { appBarStickyHeight } = useAppBarStickyHeightValue();
  const article = useFragment(
    graphql`
      fragment infographicArticleArticle on Article {
        ...globalCheckerContent
        ...landingBannerInfographicArticle
      }
    `,
    reference_,
  );

  const { trackerNode } = useAppBarScrollOverEffect({
    swapTiming: "exit",
    swapVariant: "scmp/generic-light",
    tracker: AppBarScrollOverTracker,
  });

  const isInlineAdSlotDisabled = useMobileInlineAdSlotIsDisabledFlag();

  return (
    <InfographicArticleContainer>
      <InfographicArticleWrapper>
        <LandingBanner provider={provider} reference={article} />
        <SummaryAndAuthor>
          {provider.subHeadline()}
          {provider.authorBlock()}

          <SpeechWrapper>
            {provider.speechPlayer({})} {provider.speechPlayerPortal({})}
          </SpeechWrapper>
        </SummaryAndAuthor>

        {trackerNode}

        <Content>
          {provider.body({
            extraComponentMap: {
              "inline-widget": null,
            },
            inlineAdSlots: {
              "1": {
                adUnit: "m_lrec1",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "2": {
                adUnit: "d_lrec2",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "3": {
                adUnit: "m_lrec4d",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "4": {
                adUnit: "m_lrec4e",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
              "5": {
                adUnit: "m_lrec4f",
                breakpoint: theme.breakpoints.down("desktop"),
                isDisabled: isInlineAdSlotDisabled,
              },
            },
          })}
        </Content>

        <Paywall>{provider.paywall()}</Paywall>

        <Left $stickyStackedHeight={appBarStickyHeight}>
          {provider.shareWidget({ variant: "legacyShareWithSpeech" })}
          {provider.actionWidget()}
        </Left>

        <HeaderAdSlot>
          {provider.adslot({
            adUnit: "d_banner1",
            sizes: [
              [970, 250],
              [728, 90],
              [970, 90],
            ],
          })}
        </HeaderAdSlot>

        <ClientSideSuspense>
          <GlobalChecker reference={article}>
            <Comment>{provider.commentTrigger()}</Comment>
            <Social>
              <SocialMobile>
                {provider.shareWidget({
                  variant: "legacyShareMobile",
                })}
              </SocialMobile>
              <SocialDesktop>
                {provider.shareWidget({
                  variant: "legacyShare",
                })}
              </SocialDesktop>
            </Social>

            <AuthorDetail>{provider.authorDetails()}</AuthorDetail>
            <Topic>{provider.topics()}</Topic>

            <FooterAdSlot>
              {provider.adslot({
                adUnit: "m_lrec3a",
                sizes: [[300, 250]],
                withBackground: true,
                withLabel: true,
              })}
            </FooterAdSlot>
          </GlobalChecker>
        </ClientSideSuspense>
      </InfographicArticleWrapper>
    </InfographicArticleContainer>
  );
};

InfographicArticle.displayName = "InfographicArticle";
